import { encodePainReport, fromCurrentRow, HOME_EQUIPMENT_OPTIONS, HOW_HEARD_STEP, MOTIVATION_STEP, PAIN_AREAS_STEP, } from "@repo/contract/training";
const HOME_CHIPS = new Map(HOME_EQUIPMENT_OPTIONS.map((option) => [option.equipmentId, option.value]));
export function prefillIntake(source) {
    const answers = {
        ...prefillProfile(source.profile),
        ...prefillConfig(source.config),
        ...prefillParq(source.parq),
        ...prefillInjuries(source.injuries),
        ...prefillEquipment(source.equipment),
    };
    if (typeof source.weightKg === "number" && Number.isFinite(source.weightKg)) {
        answers.weight = [String(source.weightKg)];
    }
    return answers;
}
/** The three intake facts and `D129`'s sentence. A `null` height is left
 *  unanswered so the control asks; the compile refuses a profile without it. */
function prefillProfile(profile) {
    const out = {
        date_of_birth: [profile.dateOfBirth],
        sex: [profile.sex],
    };
    if (profile.heightCm !== null) {
        out.height = [String(profile.heightCm)];
    }
    // Skipped and stored as `null` both come back as the skipped answer.
    const sentence = (profile.motivation ?? "").trim();
    out[MOTIVATION_STEP] = sentence.length === 0 ? [] : [sentence];
    // `D242`. A stored `null` is the skip; an ABSENT key is a server older
    // than the field, and the step is left unanswered so it is asked.
    if (profile.howHeard === null) {
        out[HOW_HEARD_STEP] = [];
    }
    else if (profile.howHeard !== undefined) {
        const text = (profile.howHeardOther ?? "").trim();
        out[HOW_HEARD_STEP] =
            profile.howHeard === "other" && text.length > 0
                ? [profile.howHeard, text]
                : [profile.howHeard];
    }
    return out;
}
/** The eight config-backed steps, and the experience tree fanned back out.
 *  `fixed_weekdays` only when the week is not variable, where it is on the
 *  path. */
function prefillConfig(config) {
    const out = {
        days_fixed: [config.daysFixed],
        days_per_week: [String(config.daysPerWeek)],
        experience: [config.experience.status],
        goal: [config.primaryGoal],
        secondary_goal: [config.secondaryGoal ?? "none"],
        session_hard_limit: [
            config.sessionMinutesIsHardLimit ? "hard" : "preference",
        ],
        session_minutes: [String(config.sessionMinutes)],
    };
    if (config.daysFixed !== "variable") {
        out.fixed_weekdays = config.fixedWeekdays.map(String);
    }
    const { experience } = config;
    if (experience.status === "current") {
        out.experience_consistent_for = [experience.consistentFor];
    }
    else if (experience.status === "stopped") {
        out.experience_stopped_ago = [experience.stoppedAgo];
        out.experience_trained_for = [experience.trainedFor];
    }
    return out;
}
/** Seven booleans back into the seven positional `"yes"`/`"no"` entries. */
function prefillParq(parq) {
    return { parq: parq.answers.map((answer) => (answer ? "yes" : "no")) };
}
/** The CURRENT rows become the body map's answer; the historical rows are not
 *  an intake answer (`D105`) — the commit reads them again and carries them
 *  through its write. */
function prefillInjuries(injuries) {
    const current = injuries.injuries.flatMap((row) => {
        const report = fromCurrentRow(row);
        return report === null ? [] : [report];
    });
    if (current.length === 0) {
        return { current_pain: ["no"] };
    }
    return {
        current_pain: ["yes"],
        [PAIN_AREAS_STEP]: current.map(encodePainReport),
    };
}
/**
 * The place, and the list shaped for its branch.
 *
 * A stored gym or mix answers the checklist's ids. A stored home or outdoors
 * answers the chips when every stored id is a chip; a list the chips cannot
 * show (ticked on the checklist from the Profile) is kept as ids, which the
 * chip control does not offer, so the step is asked again with the list still
 * in the answers to seed from. No stored place: inferred from the list, an
 * empty list reading as a home with nothing ticked.
 */
function prefillEquipment(equipment) {
    const ids = equipment.available.map(String);
    const chips = equipment.available.map((id) => HOME_CHIPS.get(id));
    const allChips = chips.every((chip) => chip !== undefined);
    const place = equipment.place ?? (allChips ? "home" : "gym");
    if (place === "gym" || place === "mix") {
        return { equipment: ids, equipment_place: [place] };
    }
    return {
        equipment: allChips ? chips : ids,
        equipment_place: [place],
    };
}
//# sourceMappingURL=prefill.js.map